import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Copy,
  MessageSquare,
  PlusIcon,
  RefreshCcw,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  SparklesIcon,
} from "lucide-react";
import { Component, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getPresentation, getSlide, setPresentation } from "../../api";
import DarkModeToggle from "../../components/DarkModeToggle";
import DataVisualization from "../../components/DataVisualization";
import SpateLogo from "../../components/SpateLogo";
import Bugsnag from "../../utils/bugsnag";
import { builderSamplePrompts as samplePrompts } from "../../utils/builderSamplePrompts";
import { trackEvent } from "../../utils/mixpanel";

const SlideErrorFallback = ({ id, input, slides, index, onRetry }) => {
  const deleteSlide = useDeleteSlide();

  const handleDelete = () => {
    trackEvent("Slide Delete", { input, index, reason: "error_boundary" });
    deleteSlide.mutate({ slides, index });
  };

  return (
    <SlideWrapper id={id}>
      <div className="flex items-center justify-between gap-8 mb-8">
        <p className="text-xs text-muted-foreground line-clamp-1">{input}</p>
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDelete}
            className="hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <h2 className="text-lg font-semibold mb-4">Something went wrong</h2>
      <p className="text-sm text-muted-foreground mb-4">
        There was an error rendering this slide. The error has been reported.
      </p>
      <Button variant="outline" onClick={onRetry}>
        Try Again
      </Button>
    </SlideWrapper>
  );
};

class SlideErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    Bugsnag.notify(error, (event) => {
      event.addMetadata("slide", {
        input: this.props.input,
        id: this.props.id,
        componentStack: errorInfo.componentStack,
      });
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <SlideErrorFallback
          id={this.props.id}
          input={this.props.input}
          slides={this.props.slides}
          index={this.props.index}
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}

const useGetSlide = ({ input, initialData }) =>
  useQuery({
    queryKey: ["slide", input],
    queryFn: () => getSlide({ input }),
    initialData,
    staleTime: Infinity,
  });

const getPresentationQueryKey = (presentation_id) => ["presentation", presentation_id];

const useGetPresentation = ({ presentation_id }) =>
  useQuery({
    queryKey: getPresentationQueryKey(presentation_id),
    queryFn: () => getPresentation({ presentation_id }).then((r) => {
      return { ...r, data: { ...r.data, slides: r.data.slides.filter((slide) => {
        const isPlaceholder = slide.title === '&lt;title&gt;' && !slide.exc_str;
        return !isPlaceholder;
      }) } };
    }),
  });

const useSetPresentation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ presentation_id, slides }) => setPresentation({ presentation_id, slides }),
    onMutate: async ({ presentation_id, slides }) => {
      await queryClient.cancelQueries({ queryKey: getPresentationQueryKey(presentation_id) });
      const previousData = queryClient.getQueryData(getPresentationQueryKey(presentation_id));
      queryClient.setQueryData(getPresentationQueryKey(presentation_id), (old) => ({
        ...old,
        data: {
          ...old.data,
          slides,
        },
      }));
      return { previousData };
    },
  });
};

const generateSlideId = () => `slide-${crypto.randomUUID()}`;

const useAddSlide = () => {
  const setPresentation = useSetPresentation();
  return useMutation({
    mutationFn: async ({ slides, input, index = null }) => {
      const newSlide = { id: generateSlideId(), input };
      const newSlides = [...slides];
      if (index === null || index >= slides.length) {
        newSlides.push(newSlide);
      } else {
        newSlides.splice(index, 0, newSlide);
      }
      await setPresentation.mutateAsync({ presentation_id: null, slides: newSlides });
      return newSlide.id;
    },
    onSuccess: (newSlideId) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToSlide(newSlideId);
        });
      });
    },
  });
};

const useMoveSlide = () => {
  const setPresentation = useSetPresentation();
  return useMutation({
    mutationFn: ({ slides, fromIndex, toIndex }) => {
      const newSlides = [...slides];
      const [movedSlide] = newSlides.splice(fromIndex, 1);
      newSlides.splice(toIndex, 0, movedSlide);
      return setPresentation.mutate({ presentation_id: null, slides: newSlides });
    },
  });
};

const useDeleteSlide = () => {
  const setPresentation = useSetPresentation();
  return useMutation({
    mutationFn: ({ slides, index }) => {
      const newSlides = [...slides];
      newSlides.splice(index, 1);
      return setPresentation.mutate({ presentation_id: null, slides: newSlides });
    },
  });
};

const useUpdateSlideInput = () => {
  const queryClient = useQueryClient();
  const setPresentation = useSetPresentation();
  return useMutation({
    mutationFn: ({ slides, index, newInput }) => {
      const newSlides = slides.map((slide, i) =>
        i === index ? { id: slide.id, input: newInput } : slide
      );
      queryClient.removeQueries({ queryKey: ["slide", slides[index].input] });
      return setPresentation.mutate({ presentation_id: null, slides: newSlides });
    },
  });
};

const SlideWrapper = ({ className, ...props }) => {
  const classNames =
    "p-6 pt-4 border rounded-lg shadow-sm bg-card text-card-foreground min-h-[calc(100vh-61px-var(--spacing)*8)] lg:max-w-[1200px] w-full mx-auto";
  return <div className={cn(className, classNames)} {...props} />;
};

const SERVER_ERROR_MESSAGE =
  "Something went wrong on our side. We're already tracking this error, but if it keeps happening, please reach out and we'll take care of it.";

const NO_DATA_MESSAGE = "No data found. Please adjust your input and try again.";

const getErrorMessage = (error) => {
  const status = error?.response?.status || error?.status;
  let message = SERVER_ERROR_MESSAGE;
  if (error?.response?.data?.message) {
    message = error.response.data.message;
  }
  if (message === 'no data found') {
    message = NO_DATA_MESSAGE;
  }
  if (status >= 500 && status < 600) {
    Bugsnag.notify(error);
  }
  return message;
};

const scrollToSlide = (id, { behavior = "smooth" } = {}) => {
  const slideElement = document.getElementById(id);
  if (slideElement) {
    const yOffset = -60 - 16;
    const y = slideElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior });
  }
};

const SlideToolbar = ({ index, slides, totalSlides, onRefetch, isRefetching, id, input }) => {
  const deleteSlide = useDeleteSlide();
  const moveSlide = useMoveSlide();
  const scrollAfterMove = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToSlide(id, { behavior: "instant" }));
    });
  };
  const onMoveUp = (_e) => {
    trackEvent("Slide Move", { direction: "up", input, fromIndex: index, toIndex: index - 1 });
    moveSlide.mutate({ slides, fromIndex: index, toIndex: index - 1 });
    scrollAfterMove();
  };
  const onMoveDown = (_e) => {
    trackEvent("Slide Move", { direction: "down", input, fromIndex: index, toIndex: index + 1 });
    moveSlide.mutate({ slides, fromIndex: index, toIndex: index + 1 });
    scrollAfterMove();
  };
  const onDelete = (_e) => {
    trackEvent("Slide Delete", { input, index });
    deleteSlide.mutate({ slides, index });
  };
  const handleRefetch = () => {
    trackEvent("Slide Refetch", { input });
    onRefetch();
  };
  return (
    <div className="flex items-center justify-between gap-8 mb-8">
      <div className="flex-grow">
        <EditableSlideInput input={input} slides={slides} index={index} />
      </div>
      <div className="flex items-center space-x-1">
        {index !== 0 && (
          <Button variant="ghost" size="icon" onClick={onMoveUp}>
            <ArrowUp className="h-4 w-4" />
          </Button>
        )}
        {index !== totalSlides - 1 && (
          <Button variant="ghost" size="icon" onClick={onMoveDown} disabled={index === totalSlides - 1}>
            <ArrowDown className="h-4 w-4" />
          </Button>
        )}
        {onRefetch && (
          <Button variant="ghost" size="icon" onClick={handleRefetch} disabled={isRefetching}>
            <RefreshCcw className={cn("h-4 w-4", isRefetching && "animate-spin")} />
          </Button>
        )}
        <Button variant="ghost" size="icon" onClick={onDelete} className="hover:text-destructive">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const EditableSlideInput = ({ input, slides, index }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(input);
  const updateSlideInput = useUpdateSlideInput();

  const handleSave = () => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== input) {
      trackEvent("Slide Edit Input", { oldInput: input, newInput: trimmed, index });
      updateSlideInput.mutate({ slides, index, newInput: trimmed });
    } else {
      setValue(input);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      setValue(input);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        className="text-xs text-muted-foreground bg-transparent border-b border-muted-foreground/50 outline-none w-full"
      />
    );
  }

  return (
    <p
      className="text-xs text-muted-foreground line-clamp-1 cursor-pointer hover:text-foreground transition-colors"
      onClick={() => setIsEditing(true)}
    >
      {input}
    </p>
  );
};

const SlideTitleH2 = ({ children }) => {
  return <h2 className="text-lg font-semibold mb-8 text-pretty line-clamp-3">{children}</h2>;
};

const SlideTitleHTML = ({ title }) => {
  return (
    <SlideTitleH2>
      <span dangerouslySetInnerHTML={{ __html: title }} />
    </SlideTitleH2>
  );
};

const StoryToolbar = ({ story, input }) => {
  const [copied, setCopied] = useState(false);
  const [vote, setVote] = useState(null); // 'up' | 'down' | null
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(story);
      trackEvent("Slide Copy", { input, story });
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleVote = (type) => {
    const newVote = vote === type ? null : type;
    setVote(newVote);
    if (newVote) {
      trackEvent("Slide Feedback", {
        type: newVote === "up" ? "thumbs_up" : "thumbs_down",
        input,
        story,
      });
    }
  };

  const handleSendFeedback = () => {
    if (!feedback.trim()) return;
    trackEvent("Slide Feedback", {
      type: "text_feedback",
      input,
      story,
      feedback: feedback.trim(),
    });
    setShowFeedback(false);
    setFeedback("");
  };

  return (
    <div className="mt-4 flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleCopy}>
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? "Copied!" : "Copy story"}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-8 w-8", vote === "up" && "text-green-600")}
            onClick={() => handleVote("up")}
          >
            <ThumbsUp className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Helpful</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-8 w-8", vote === "down" && "text-red-600")}
            onClick={() => handleVote("down")}
          >
            <ThumbsDown className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Not helpful</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setShowFeedback(true)}>
            <MessageSquare className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Send feedback</TooltipContent>
      </Tooltip>

      <Dialog open={showFeedback} onOpenChange={setShowFeedback}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Feedback</DialogTitle>
            <DialogDescription>
              Help us improve by sharing your thoughts on this slide.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="What could be better about this slide?"
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedback(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendFeedback} disabled={!feedback.trim()}>
              Send Feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SlideTitle = ({ slide }) => {
  return slide?.title_simple !== "failed generating title" ? (
    <SlideTitleHTML title={slide.title} />
  ) : (
    <SlideTitleH2>{slide.input}</SlideTitleH2>
  );
};

const Slide = ({ input, index, totalSlides, slides, initialData, id }) => {
  const setPresentation = useSetPresentation();
  const slideQuery = useGetSlide({ input, initialData });
  const { data, isSuccess, isFetching } = slideQuery;

  useEffect(() => {
    if (isSuccess && data) {
      setPresentation.mutate({
        presentation_id: null,
        slides: slides.map((slide) =>
          slide.input === input && slide.id === id
            ? ({ ...data, input, id })
            : slide
        ),
      });
    }
  }, [isSuccess, data, id]);

  if (slideQuery.isLoading)
    return (
      <SlidePlaceholder
        index={index}
        totalSlides={totalSlides}
        slides={slides}
        id={id}
        input={input}
      />
    );
  if (slideQuery.isError || slideQuery.data?.exc_str)
    return (
      <SlideWrapper id={id}>
        <SlideToolbar
          slides={slides}
          index={index}
          totalSlides={totalSlides}
          onRefetch={() => slideQuery.refetch()}
          isRefetching={isFetching}
          id={id}
          input={input}
        />
        <SlideTitleH2>
          {slideQuery.data?.exc_str
            ? 'Unable to load slide'
            : 'Error loading slide'}
        </SlideTitleH2>
        {slideQuery.data?.exc_str ? (
          <p className="mb-2">{slideQuery.data?.exc_str}</p>
        ) : (
          <p className="mb-2">{getErrorMessage(slideQuery.error)}</p>
        )}
      </SlideWrapper>
    );
  const slide = slideQuery.data;
  return (
    <SlideWrapper id={id}>
      <SlideToolbar
        slides={slides}
        index={index}
        totalSlides={totalSlides}
        onRefetch={() => slideQuery.refetch()}
        isRefetching={isFetching}
        id={id}
        input={input}
      />
      <div className="mb-4 text-sm flex flex-col gap-16 lg:grid lg:grid-cols-[1fr_minmax(35rem,_1fr)] lg:gap-16">
        <div>
          <SlideTitle slide={{ ...slide, input }} />
          <div className="text-foreground prose prose-sm prose-neutral dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{slide.story}</ReactMarkdown>
          </div>
          <StoryToolbar story={slide.story} input={input} />
        </div>
        <DataVisualization data={slide.data} />
      </div>
    </SlideWrapper>
  );
};

const TopBar = () => {
  return (
    <div className="p-4 border-b flex items-center start gap-2 sticky top-0 bg-background z-10">
      <span>
        <a href="https://app.spate.nyc">
          <SpateLogo className="mx-auto h-6 w-auto inline-block" />
        </a>
      </span>
      <span className="border-l border-muted-foreground mx-2" />
      <span className="inline-block">
        <span className="ml-2 text-lg font-semibold">
          Report Builder
          <span className="text-primary font-light ml-[.5ch]">Beta</span>
        </span>
      </span>
      <span className="ml-auto border-l border-muted-foreground mx-2" />
      <DarkModeToggle />
    </div>
  );
};

const MiniSlide = ({ onClick, isActive, index, title, isLoading, isRefetching }) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "border rounded-lg aspect-[4/3] hover:shadow-md transition-all duration-200 snap-center cursor-pointer flex items-center justify-center",
            isActive
              ? "border-primary bg-primary/10 shadow-md"
              : "dark:bg-card dark:text-card-foreground",
            (isLoading || isRefetching) && "animate-pulse"
          )}
          onClick={onClick}
        >
          <span
            className={cn(
              "text-sm font-medium",
              isActive ? "text-primary" : "text-muted-foreground"
            )}
          >
            {index + 1}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        <p className="line-clamp-3">{title}</p>
      </TooltipContent>
    </Tooltip>
  );
};

const MiniSlideWithQuery = ({ slide, index, isActive, onClick }) => {
  const { input, id, ...initialData } = slide;
  const slideQuery = useGetSlide({ input, initialData: Object.keys(initialData).length > 0 ? initialData : undefined });
  const title = slideQuery.data?.title_simple || slideQuery.data?.title_simple || input || "Slide";
  return (
    <MiniSlide
      index={index}
      title={title}
      isActive={isActive}
      onClick={onClick}
      isLoading={slideQuery.isLoading}
      isRefetching={slideQuery.isFetching && !slideQuery.isLoading}
    />
  );
};

const AddSlideModal = ({ open, onOpenChange, onAddSlide, slides, insertIndex }) => {
  const inputRef = useRef(null);
  const handlePromptClick = (prompt) => {
    if (inputRef.current) {
      inputRef.current.value = prompt;
      inputRef.current.focus();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const input = formData.get("input");
    if (!input?.trim()) return;

    trackEvent("Slide Add", { input, source: "custom_input", index: insertIndex });
    onAddSlide({ slides, input, index: insertIndex });
    event.target.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate a new slide</DialogTitle>
          <DialogDescription>
            What should this slide be about?
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Textarea
            id="input"
            name="input"
            placeholder="e.g., Tell me about the latest trends in skincare."
            className="min-h-[100px] resize-none text-sm"
            ref={inputRef}
            required
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                e.currentTarget.form.requestSubmit();
              }
            }}
          />

          <div className="text-sm text-muted-foreground">
            Or try one of these examples:
          </div>

          <div className="flex flex-wrap gap-2">
            {samplePrompts.map((prompt, idx) => {
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handlePromptClick(prompt.fullPrompt)}
                  className="group relative inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs transition-all hover:bg-accent hover:border-primary/20"
                >
                  <span className="font-medium">{prompt.label}</span>
                </button>
              );
            })}
          </div>

          <DialogFooter>
            <Button type="submit" className="w-full">
              <SparklesIcon className="h-4 w-4 mr-2" />
              Generate slide
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EmptyState = ({ onAddSlide }) => {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <h2 className="text-xl font-semibold mb-2">No slides yet</h2>
        <p className="text-muted-foreground mb-6">
          Get started by generating your first slide. You can ask about categories, brands, markets, platforms, and more.
        </p>
        <Button onClick={onAddSlide}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Generate your first slide
        </Button>
      </div>
    </div>
  );
};

const SlideNavigation = ({ slides, activeSlideIndex, onAddSlideClick }) => {
  const scrollPanelRef = useRef();
  const miniSlideRefs = useRef({});
  const [moreToScroll, setMoreToScroll] = useState(false);

  useEffect(() => {
    const activeSlide = slides[activeSlideIndex];
    const element = miniSlideRefs.current[activeSlide?.id];
    if (element && scrollPanelRef.current) {
      const container = scrollPanelRef.current;
      const elementTop = element.offsetTop;
      const elementHeight = element.offsetHeight;
      const containerHeight = container.clientHeight;
      const scrollTop = elementTop - containerHeight / 2 + elementHeight / 2;
      container.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    }
  }, [activeSlideIndex, slides]);

  const onScroll = () => {
    if (scrollPanelRef.current) {
      const canScroll = scrollPanelRef.current.scrollHeight > scrollPanelRef.current.clientHeight;
      const atBottom =
        scrollPanelRef.current.scrollHeight <=
        scrollPanelRef.current.clientHeight + scrollPanelRef.current.scrollTop + 1;
      const nextMoreToScroll = canScroll && !atBottom;
      if (nextMoreToScroll !== moreToScroll) setMoreToScroll(nextMoreToScroll);
    }
  };
  return (
    <div className="flex flex-col basis-25 shrink-0 border-r sticky top-[61px] h-[calc(100vh-61px)] bg-background">
      {slides.length > 0 ? (
        <div
          className="gap-2 p-4 flex flex-col overflow-y-auto snap-y scrollbar-none"
          ref={scrollPanelRef}
          onScroll={onScroll}
        >
          {slides.map((slide, index) => (
            <div key={slide.id} ref={(el) => (miniSlideRefs.current[slide.id] = el)}>
              <MiniSlideWithQuery
                slide={slide}
                index={index}
                isActive={index === activeSlideIndex}
                onClick={() => scrollToSlide(slide.id)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="min-h-4" />
      )}
      <div className={cn("relative", moreToScroll ? "block" : "hidden")}>
        <div className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none bg-gradient-to-t from-background to-transparent" />
      </div>
      <div>
        <div className="p-4 pt-0 flex flex-col">
          <button
            className="bg-primary text-primary-foreground rounded-lg aspect-[4/3] items-center justify-center flex hover:shadow-md transition-shadow duration-200"
            onClick={onAddSlideClick}
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const FullSizeSlides = ({ slides }) => {
  return (
    <div className="flex-1 flex flex-col gap-4 p-4">
      {slides.map((slide, index) => {
        const { id, input, ...initialData } = slide;
        return (
          <SlideErrorBoundary key={id} id={id} input={input} slides={slides} index={index}>
            <Slide
              input={input}
              slides={slides}
              index={index}
              totalSlides={slides.length}
              initialData={Object.keys(initialData).length > 0 ? initialData : undefined}
              id={id}
            />
          </SlideErrorBoundary>
        );
      })}
    </div>
  );
};

const MiniSlidePlaceholder = () => {
  return <div className="border border-dashed rounded-lg aspect-[4/3] animate-pulse bg-muted" />;
};

const SlidePlaceholder = ({ id, index, totalSlides, slides, input }) => {
  return (
    <SlideWrapper id={id}>
      {(index || index === 0) && totalSlides && slides ? (
        <SlideToolbar index={index} slides={slides} totalSlides={totalSlides} onRefetch={null} input={input} id={id} />
      ) : null}
      <div className="h-6 bg-muted rounded w-1/3 mb-8 animate-pulse" />
      <div className="grid grid-cols-[1fr_minmax(25rem,_1fr)] gap-4 mb-4">
        <div className="space-y-2">
          <div className="h-4 bg-muted rounded w-full animate-pulse" />
          <div className="h-4 bg-muted rounded w-5/6 animate-pulse" />
          <div className="h-4 bg-muted rounded w-2/3 animate-pulse" />
          <div className="h-4 bg-muted rounded w-4/5 animate-pulse" />
          <div className="h-4 bg-muted rounded w-1/2 animate-pulse" />
        </div>
        <div className="h-48 bg-muted rounded animate-pulse" />
      </div>
    </SlideWrapper>
  );
};

const BuilderLayoutLoading = () => {
  return (
    <div>
      <TopBar />
      <div className="flex flex-row min-h-screen">
        <div className="basis-25 shrink-0 border-r sticky top-[61px] h-[calc(100vh-61px)] bg-background p-4">
          <div className="text-muted-foreground">
            <MiniSlidePlaceholder />
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-4 p-4">
          <SlidePlaceholder />
        </div>
      </div>
    </div>
  );
};

const BuilderLayoutError = ({ error }) => {
  return (
    <div>
      <TopBar />
      <div className="flex flex-row min-h-screen">
        <div className="basis-25 shrink-0 border-r sticky top-[61px] h-[calc(100vh-61px)] bg-background p-4">
          <div className="text-muted-foreground">
            <MiniSlidePlaceholder />
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-4 p-4">
          <div className="p-6 pt-4 border rounded-lg shadow-sm bg-card text-card-foreground min-h-[calc(100vh-61px-var(--spacing)*8)]">
            <SlideTitleH2>Error loading presentation</SlideTitleH2>
            <p className="mb-2">{getErrorMessage(error)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BuilderLayout = ({ slides, onAddSlide, isReady }) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (isReady && slides.length === 0 && !hasAutoOpened.current) {
      setShowAddModal(true);
      hasAutoOpened.current = true;
    }
  }, [isReady, slides.length]);

  useEffect(() => {
    const getSlideElements = () => {
      return slides.map(({ id }) => document.getElementById(id)).filter(Boolean);
    };

    const updateActiveSlide = () => {
      const slideElements = getSlideElements();
      if (slideElements.length === 0) return;
      const targetY = window.innerHeight * 0.2;
      let closestIndex = null;
      let closestDistance = Infinity;
      slideElements.forEach((el, index) => {
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.top - targetY);
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

        if (isVisible && distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      if (closestIndex !== null) {
        setActiveSlideIndex(closestIndex);
      }
    };
    updateActiveSlide();
    window.addEventListener("scroll", updateActiveSlide, { passive: true });
    return () => {
      window.removeEventListener("scroll", updateActiveSlide);
    };
  }, [slides]);

  const handleAddSlideClick = () => {
    setShowAddModal(true);
  };

  const insertIndex = slides.length > 0 ? activeSlideIndex + 1 : null;

  return (
    <div>
      <TopBar />
      <div className="flex flex-row">
        <SlideNavigation
          slides={slides}
          activeSlideIndex={activeSlideIndex}
          onAddSlideClick={handleAddSlideClick}
        />
        {slides.length > 0 ? (
          <FullSizeSlides slides={slides} />
        ) : (
          <EmptyState onAddSlide={handleAddSlideClick} />
        )}
      </div>
      <AddSlideModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        onAddSlide={onAddSlide}
        slides={slides}
        insertIndex={insertIndex}
      />
    </div>
  );
};

const Presentation = () => {
  const presentationQuery = useGetPresentation({ presentation_id: null });
  const addSlide = useAddSlide();
  const slides = presentationQuery.data?.data?.slides || [];

  if (presentationQuery.isLoading) {
    return <BuilderLayoutLoading />;
  }

  if (presentationQuery.isError) {
    return <BuilderLayoutError error={presentationQuery.error} />;
  }
  return <BuilderLayout slides={slides} onAddSlide={addSlide.mutate} isReady />;
};

export default Presentation;
